import React from "react";
import { cn } from "@/lib/utils";
import Breadcrumbs, { BreadcrumbsProps } from "@/components/ui/breadcrumbs";
import PrimaryButton, {
  PrimaryButtonProps,
} from "@/components/ui/primary-button";
import SecondaryButton, {
  SecondaryButtonProps,
} from "@/components/ui/secondary-button";
import Image, { ImageProps } from "next/image";
import HeaderImage from "@/assets/header-image.jpeg";
import { getIcon } from "@/lib/get-icon";

type Contact = {
  icon: "map-pin" | "phone" | "mail" | "clock";
  text: string;
  url: string;
  id?: string;
};

export interface HeaderLocationsProps {
  id?: string;
  className?: string;
  headingClassName?: string;
  subheadingClassName?: string;
  heading?: string;
  subheading?: string;
  contactList?: Contact[];
  showPrimaryCTA?: boolean;
  primaryCTA?: PrimaryButtonProps;
  secondaryCTA?: SecondaryButtonProps;
  showSecondaryCTA?: boolean;
  breadcrumbs?: BreadcrumbsProps;
  image?: ImageProps;
}
const HeaderLocations: React.FC<HeaderLocationsProps> = ({
  id = "header-locations",
  className,
  headingClassName = "",
  subheadingClassName = "",
  heading = "Heading",
  subheading = "Subheading",
  contactList,
  breadcrumbs,
  showPrimaryCTA = true,
  primaryCTA = {
    color: "orange",
    className: "w-fit",
    children: "Become Our Partner",
    href: "#",
  },
  showSecondaryCTA = true,
  secondaryCTA = {
    className: "w-fit",
    color: "teal",
    children: "Learn More",
    href: "#",
  },
  image = {
    src: HeaderImage,
    alt: "Header Image",
  },
}) => {
  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto flex w-full max-w-lg flex-col gap-2 bg-white p-4 md:gap-4 md:px-6 md:py-8 lg:gap-6 lg:px-3 lg:py-10",
        className
      )}
    >
      <div>
        {breadcrumbs && breadcrumbs.data && breadcrumbs.data.length > 0 && (
          <Breadcrumbs {...breadcrumbs} />
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 md:gap-6 lg:gap-8">
        <div className="col-span-full flex w-full flex-col gap-4 md:gap-6 lg:col-span-1 lg:flex-row lg:gap-8">
          <div className="flex flex-col gap-4 lg:w-[624px] lg:gap-6">
            <hgroup className="flex flex-col gap-2 text-center md:gap-3 md:text-start lg:gap-4">
              <h1
                className={cn(
                  "font-montserrat text-heading-3 text-secondary-500 md:text-heading-2 lg:text-heading-1",
                  headingClassName
                )}
              >
                {heading}
              </h1>
              <p
                className={cn(
                  "font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2 lg:text-subheading-1",
                  subheadingClassName
                )}
              >
                {subheading}
              </p>
            </hgroup>
            {contactList && contactList.length > 0 && (
              <ul className="flex flex-col gap-2 text-neutral-500">
                {contactList.map((contact, index) => (
                  <li
                    key={`contact-${contact.text}-${index}`}
                    className="flex cursor-pointer flex-row items-center gap-2 font-roboto text-body-1-300"
                  >
                    {getIcon({
                      name: contact.icon as
                        | "map-pin"
                        | "phone"
                        | "mail"
                        | "clock",
                      className: "w-4 h-4",
                      stroke: "#06A6AB",
                    })}
                    <a
                      href={contact.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {contact.text}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            <div className="mx-auto hidden flex-col items-center gap-3 md:mx-0 md:flex-row md:items-start md:gap-4 lg:flex lg:gap-6">
              {showPrimaryCTA && primaryCTA && (
                <PrimaryButton
                  color="orange"
                  size="md"
                  {...primaryCTA}
                  id={primaryCTA.id ?? "primary-cta-subpage-header"}
                />
              )}
              {showSecondaryCTA && secondaryCTA && (
                <SecondaryButton
                  color="teal"
                  size="md"
                  {...secondaryCTA}
                  id={secondaryCTA.id ?? "secondary-cta-subpage-header"}
                />
              )}
            </div>
          </div>
        </div>
        {image && (
          <div
            className={cn(
              "relative order-none col-span-full h-full justify-center overflow-clip rounded-3xl shadow-[4px_4px_0px_0px_#FFD5BF] md:shadow-[6px_6px_0px_0px_#FFD5BF] lg:order-none lg:col-span-1 lg:shadow-[8px_8px_0px_0px_#FFD5BF]",
              "md:min-h-0000px] mx-auto min-h-[200px] w-full flex-1 flex-col bg-center lg:min-h-[424px]"
            )}
          >
            {image && (
              <Image
                className="object-cover"
                {...image}
                alt={image?.alt}
                fill
                priority
              />
            )}
          </div>
        )}
        <div className="col-span-2 mx-auto flex flex-col items-center gap-3 md:mx-0 md:flex-row md:items-start md:gap-4 lg:hidden lg:gap-6">
          {showPrimaryCTA && primaryCTA && (
            <PrimaryButton
              color="orange"
              size="md"
              {...primaryCTA}
              id={primaryCTA.id ?? "primary-cta-subpage-header"}
            />
          )}
          {showSecondaryCTA && secondaryCTA && (
            <SecondaryButton
              color="teal"
              size="md"
              {...secondaryCTA}
              id={secondaryCTA.id ?? "secondary-cta-subpage-header"}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default HeaderLocations;
